import React, { useCallback, useMemo } from 'react';

import { Box, Center, Container } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import {
  IconArrowNarrowDown,
  IconClock,
  IconFunction,
  IconLetterCase,
  IconMail,
  IconPencil,
  IconUser,
} from '@tabler/icons-react';
import { useLocalization } from 'context/localization-context';

import FlowBuilderCard from './FlowBuilderCard';
import { formatFlowDeliveryModeText } from '../../Flows/components/FlowDeliveryModeFormatter';
import { useStyles } from '../styles';
import { CardTypes, FormValues } from '../types';

interface IFlowBuilderMainContainerProps {
  selectedCard: CardTypes | null;
  onCardSelect: (cardType: CardTypes) => void;
  form: UseFormReturnType<FormValues, (values: FormValues) => FormValues>;
  permission?: boolean;
}

const FlowBuilderMainContainer = ({
  onCardSelect,
  form,
  selectedCard,
  permission = true,
}: IFlowBuilderMainContainerProps): JSX.Element => {
  const { classes } = useStyles();
  const { timeFormat } = useLocalization();

  const cardTypes: CardTypes[] = [
    'trigger',
    'delivery',
    'recipients',
    'notificationType',
    'notificationMessageTemplate',
  ];

  const getPlaceholderText = (cardType: CardTypes): string => {
    switch (cardType) {
      case 'trigger':
        return '+ select trigger';
      case 'delivery':
        return '+ wait';
      case 'recipients':
        return '+ select recipients';
      case 'notificationType':
        return '+ select notification type';
      case 'notificationMessageTemplate':
        return '+ select notification template';
      default:
        return '+ select option';
    }
  };

  const getIcon = (cardType: CardTypes): React.ReactNode => {
    switch (cardType) {
      case 'trigger':
        return <IconFunction />;
      case 'recipients':
        return <IconUser />;
      case 'notificationType':
        return <IconMail />;
      case 'notificationMessageTemplate':
        return <IconLetterCase />;
      case 'delivery':
        return <IconClock />;
      default:
        return <IconPencil />;
    }
  };

  const getCardStyle = (cardType: CardTypes): string => {
    if (!permission) {
      return classes.lockedCard;
    } else if (cardType == selectedCard) {
      return classes.selectedCard;
    } else if (cardType == 'notificationMessageTemplate' && !form.values.notificationType) {
      return classes.lockedCard;
    } else {
      return classes.clickableCard;
    }
  };

  const formattedFlowDeliveryModeText = useMemo(() => {
    return formatFlowDeliveryModeText({
      trigger: form.values.trigger,
      timeUnit: form.values.timeUnit,
      timeAmount: form.values.timeAmount,
      deliveryMode: form.values.deliveryMode,
      sendAtTime: form.values.sendAtTime,
      timeFormat,
    });
  }, [
    form.values.deliveryMode,
    form.values.sendAtTime,
    form.values.timeAmount,
    form.values.timeUnit,
    form.values.trigger,
    timeFormat,
  ]);

  const getSelectedOptionFromCardType = useCallback(
    (cardType: string | null): string | null => {
      switch (cardType) {
        case 'trigger': {
          return form.values.trigger;
        }
        case 'delivery': {
          return formattedFlowDeliveryModeText;
        }
        case 'recipients': {
          return form.values.recipients;
        }
        case 'notificationType': {
          return form.values.notificationType;
        }
        default: {
          return form.values.notificationMessageTemplate.label;
        }
      }
    },
    [
      form.values.notificationMessageTemplate.label,
      form.values.notificationType,
      form.values.recipients,
      form.values.trigger,
      formattedFlowDeliveryModeText,
    ],
  );

  return (
    <Container style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center' }}>
      <Center style={{ flexDirection: 'column', gap: '20px', maxWidth: '400px', width: '100%' }}>
        {cardTypes.map((cardType, index) => (
          <>
            <FlowBuilderCard
              onCardSelect={onCardSelect}
              selectedOption={getSelectedOptionFromCardType(cardType)}
              cardType={cardType}
              placeholderText={getPlaceholderText(cardType)}
              cardStyle={getCardStyle(cardType)}
              disabled={!permission}
            >
              {getIcon(cardType)}
            </FlowBuilderCard>
            {index < 4 && (
              <Box>
                <IconArrowNarrowDown size={20} />
              </Box>
            )}
          </>
        ))}
      </Center>
    </Container>
  );
};

export default FlowBuilderMainContainer;
